import React from 'react';
import { TextInput, TextArea } from '@patternfly/react-core';

export interface BaseInputProps {
    value: string;
    onChange: (value: string) => void;
    onBlur?: () => void;
    validated?: 'default' | 'error' | 'warning' | 'success';
    isRequired?: boolean;
    placeholder?: string;
    ariaLabel?: string;
    error?: string;
    disabled?: boolean;
    type?: 'text' | 'textarea';
    rows?: number;
    maxLength?: number;
}

export const BaseInput: React.FC<BaseInputProps> = ({
    value,
    onChange,
    onBlur,
    validated = 'default',
    isRequired = false,
    placeholder,
    ariaLabel,
    error,
    disabled = false,
    type = 'text',
    rows = 4,
    maxLength
}) => {
    const handleChange = (
        event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>,
        newValue: string
    ) => {
        onChange(newValue);
    };

    // Determine validated state
    const getValidatedState = (): 'default' | 'success' | 'warning' | 'error' => {
        if (error) return 'error';
        return validated;
    };

    const commonProps = {
        'aria-label': ariaLabel,
        placeholder,
        isRequired,
        value,
        onChange: handleChange,
        onBlur,
        validated: getValidatedState(),
        isDisabled: disabled,
        ...(maxLength && { maxLength })
    };

    return (
        <>
            {type === 'textarea' ? (
                <TextArea
                    {...commonProps}
                    rows={rows}
                    resizeOrientation="vertical"
                />
            ) : (
                <TextInput {...commonProps} />
            )}
            {error && (
                <div className="dx-helper-text dx-helper-text--error">{error}</div>
            )}
        </>
    );
};

export interface UseBaseInputOptions {
    required?: boolean;
    minLength?: number;
    maxLength?: number;
    pattern?: RegExp;
    patternErrorMessage?: string;
    customValidator?: (value: string) => string;
    requiredMessage?: string;
}

export interface UseBaseInputReturn {
    value: string;
    error: string;
    handleChange: (value: string) => void;
    handleBlur: () => void;
    validate: () => string;
    setError: (error: string) => void;
    setValue: (value: string) => void;
    isValid: boolean;
}

export const useBaseInput = (options: UseBaseInputOptions = {}): UseBaseInputReturn => {
    const {
        required = false,
        minLength,
        maxLength,
        pattern,
        patternErrorMessage = 'Invalid format',
        customValidator,
        requiredMessage = 'This field is required'
    } = options;

    const [value, setValue] = React.useState<string>('');
    const [error, setError] = React.useState<string>('');

    const validate = (inputValue?: string): string => {
        const currentValue = inputValue !== undefined ? inputValue : value;
        
        // Required validation
        if (required && !currentValue.trim()) {
            return requiredMessage;
        }
        
        // Skip other validations if not required and empty
        if (!required && !currentValue.trim()) {
            return '';
        }
        
        // Min length validation
        if (minLength && currentValue.trim().length < minLength) {
            return `Must be at least ${minLength} characters`;
        }
        
        // Max length validation
        if (maxLength && currentValue.trim().length > maxLength) {
            return `Cannot exceed ${maxLength} characters`;
        }
        
        // Pattern validation
        if (pattern && !pattern.test(currentValue.trim())) {
            return patternErrorMessage;
        }
        
        // Custom validation
        if (customValidator) {
            const customError = customValidator(currentValue);
            if (customError) {
                return customError;
            }
        }
        
        return '';
    };

    const handleChange = (newValue: string) => {
        setValue(newValue);
        // Clear error when user starts typing
        if (error && newValue) {
            setError('');
        }
    };

    const handleBlur = () => {
        const validationError = validate();
        setError(validationError);
    };

    const validateField = (): string => {
        const validationError = validate();
        setError(validationError);
        return validationError;
    };

    return {
        value,
        error,
        handleChange,
        handleBlur,
        validate: validateField,
        setError,
        setValue,
        isValid: !error && (!required || !!value)
    };
};

// Generic interface for specific input components
export interface BaseInputComponentProps extends Omit<BaseInputProps, 'onChange'> {
    value: string;
    onChange: (value: string) => void;
    // Support for dynamic props like techStack for ServiceNameInput
    [key: string]: any;
}

// Factory function to create input components with less boilerplate
export const createInputComponent = (
    name: string,
    config: {
        type?: 'text' | 'textarea';
        rows?: number;
        maxLength?: number;
        getPlaceholder?: (props: any) => string;
        getAriaLabel?: (props: any) => string;
        defaultPlaceholder?: string;
    } = {}
) => {
    const InputComponent: React.FC<BaseInputComponentProps> = ({
        value,
        onChange,
        placeholder,
        ariaLabel,
        ...props
    }) => {
        // Determine placeholder dynamically if getPlaceholder is provided
        const finalPlaceholder = placeholder || 
            (config.getPlaceholder ? config.getPlaceholder(props) : config.defaultPlaceholder);
        
        // Determine aria label dynamically if getAriaLabel is provided
        const finalAriaLabel = ariaLabel || 
            (config.getAriaLabel ? config.getAriaLabel(props) : name);

        return (
            <BaseInput
                {...props}
                value={value}
                onChange={onChange}
                placeholder={finalPlaceholder}
                ariaLabel={finalAriaLabel}
                type={config.type}
                rows={config.rows}
                maxLength={config.maxLength}
            />
        );
    };

    InputComponent.displayName = name;
    return InputComponent;
};
